import JSZip from "jszip"

import { CASE_ZIP_FOLDERS } from "@/gluelayer/core/common/zipFileNames"

/**
 * Entries an uploaded case zip must contain:
 * - its top-level folder ("Setting Data0/"): weDesign refuses a case whose
 *   extracted folders are missing ("Local case file is not complete."), and an
 *   empty zip creates none
 * - files a zip rebuilt from a virtual FS that never loaded its folder misses
 *   (see bulk2.zip overwrite incident). Only list files the frontend never
 *   recreates: default resource files (Setting Data2.version.json,
 *   CuttinglineSetting.json, elasticControlPoints.json) are restored by
 *   saveAndGetFileZips and can't reveal an incomplete zip.
 * An entry ending with "/" is a folder: any entry under it counts.
 */
export const REQUIRED_ZIP_ENTRIES: Readonly<Record<string, readonly string[]>> =
  Object.fromEntries(
    Object.entries(CASE_ZIP_FOLDERS).map(([zipName, folder]) => [
      `${zipName}.zip`,
      zipName === "bulk2"
        ? [`${folder}/`, `${folder}/ArchType.json`]
        : [`${folder}/`],
    ]),
  )

const hasEntry = (zip: JSZip, entry: string): boolean =>
  entry.endsWith("/")
    ? Object.keys(zip.files).some((path) => path.startsWith(entry))
    : !!zip.file(entry)

/**
 * Log-only until the required list is agreed: refusing the upload could block
 * flows whose generated zips were not verified (e.g. new case creation).
 */
export const IS_REQUIRED_ZIP_ENTRIES_ENFORCED = false

export const hasRequiredZipEntries = (fileName: string): boolean =>
  !!REQUIRED_ZIP_ENTRIES[fileName]?.length

/**
 * @param file the zip, or the zip already parsed by the caller
 * @returns the required entries missing from the zip, [] when none is required
 * @throws when the zip can't be read
 */
export async function findMissingRequiredZipEntries(
  file: Blob | JSZip,
  fileName: string,
): Promise<string[]> {
  const requiredEntries = REQUIRED_ZIP_ENTRIES[fileName]
  if (!requiredEntries?.length) return []

  const zip = file instanceof JSZip ? file : await JSZip.loadAsync(file)
  return requiredEntries.filter((entry) => !hasEntry(zip, entry))
}
